<template>
    <div class="email-auth-list">
        <div v-if="isEmptyState && !isLoading" class="empty-state-container">
            <EmailAuthEmptyStateCard @add-email-auth="addDomain" />

            <EmailAuthGoDaddyDomainCard />
        </div>
        <DsPlaceholder
            :loading="isLoading"
            :rows="[
                { height: '1rem', boxes: [1, 1] },
                { height: '3rem', boxes: [1] },
                { height: '3rem', boxes: [1] },
            ]"
        />
        <div v-if="!isLoading">
            <section v-if="issueDomains.size > 0" class="issue-domains">
                <h3>{{ $t('email.auth.list.sectionHeaders.issues') }}</h3>

                <DsCard class="auth-status-section">
                    <EmailAuthListItem
                        v-for="[key] in issueDomains"
                        :key="key"
                        :domain="{ domain: key }"
                        status="issue"
                        @edit-email-auth="authenticate"
                    />
                </DsCard>
            </section>

            <section v-if="suggestedDomains.size > 0" class="suggested-domains">
                <h3>{{ $t('email.auth.list.sectionHeaders.suggested') }}</h3>

                <DsCard class="auth-status-section">
                    <EmailAuthListItem
                        v-for="[key] in suggestedDomains"
                        :key="key"
                        :domain="{ domain: key }"
                        status="suggested"
                        @add-email-auth="authenticate"
                    />
                </DsCard>
            </section>

            <section v-if="pendingDomains.size > 0" class="pending-domains">
                <h3>{{ $t('email.auth.list.sectionHeaders.pending') }}</h3>

                <DsCard class="auth-status-section">
                    <EmailAuthListItem
                        v-for="[key, records] in pendingDomains"
                        :key="key"
                        :domain="{ domain: key, id: Object.values(records.records)[0].id, emailAuthType: Object.values(records.records)[0].emailAuthType }"
                        status="pending"
                        @refresh-email-auth="retry"
                        @show-status-email-auth="showStatus"
                        @edit-email-auth="authenticate"
                        @delete-email-auth="remove"
                    />
                </DsCard>
            </section>

            <section v-if="connectedDomains.size > 0" class="connected-domains">
                <h3>{{ $t('email.auth.list.sectionHeaders.connected') }}</h3>

                <DsCard class="auth-status-section">
                    <EmailAuthListItem
                        v-for="[key, records] in connectedDomains"
                        :key="key"
                        :domain="{ domain: key, id: Object.values(records.records)[0].id, emailAuthType: Object.values(records.records)[0].emailAuthType }"
                        status="connected"
                        @show-status-email-auth="showStatus"
                        @edit-email-auth="authenticate"
                        @delete-email-auth="remove"
                    />
                </DsCard>
            </section>
        </div>
    </div>
</template>

<script>
import EmailAuthEmptyStateCard from '@/components/settings/email/EmailAuthEmptyStateCard';
import EmailAuthListItem from '@/components/settings/email/EmailAuthListItem';
import {
    EMAIL_AUTH_TYPE_LEGACY, REQUIRED_RECORD_TYPES,
} from '@/components/settings/settings.constants';
import EmailAuthGoDaddyDomainCard from '@/components/settings/email/EmailAuthGoDaddyDomainCard';

export default {
    components: {
        EmailAuthEmptyStateCard,
        EmailAuthListItem,
        EmailAuthGoDaddyDomainCard,
    },

    props: {
        domains: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
    },

    emits: [
        'add-domain',
        'authenticate',
        'show-status',
        'remove',
        'retry',
    ],

    computed: {
        issueDomains() {
            return new Map([...this.domainsMapped]
                .filter(
                    ([domain]) => !this.connectedDomains.has(domain)
                        && !this.pendingDomains.has(domain)
                        && !this.suggestedDomains.has(domain),
                ));
        },

        suggestedDomains() {
            return new Map([...this.domainsMapped]
                .filter(([, records]) => records.records[EMAIL_AUTH_TYPE_LEGACY]?.id === 0));
        },

        pendingDomains() {
            return new Map([...this.domainsMapped]
                .filter(
                    ([, records]) => REQUIRED_RECORD_TYPES.every((requiredType) => Object.hasOwn(records.records, requiredType))
                        && REQUIRED_RECORD_TYPES.some((requiredType) => records.records[requiredType].verified === false),
                ));
        },

        connectedDomains() {
            return new Map([...this.domainsMapped]
                .filter(
                    ([, records]) => REQUIRED_RECORD_TYPES.every((key) => Object.hasOwn(records.records, key)
                        && records.records[key].verified === true),
                ));
        },

        isEmptyState() {
            return this.domainsMapped.size === this.suggestedDomains.size;
        },

        domainsMapped() {
            return this.domains.reduce(
                (map, domain) => {
                    if (map.has(domain.domain)) {
                        const existing = map.get(domain.domain);

                        const recordSet = {
                            records: {
                                ...existing.records,
                                [domain.emailAuthType]: domain,
                            },
                        };

                        return map.set(domain.domain, recordSet);
                    }

                    const recordSet = {
                        records: {
                            [domain.emailAuthType]: domain,
                        },
                    };

                    return map.set(domain.domain, recordSet);
                },
                new Map(),
            );
        },
    },

    methods: {
        addDomain(domain) {
            this.$emit('add-domain', domain);
        },

        authenticate(domain) {
            this.$emit('authenticate', domain);
        },

        showStatus(domain) {
            this.$emit('show-status', domain);
        },

        remove(domain) {
            this.$emit('remove', domain);
        },

        retry(domain) {
            this.$emit('retry', domain);
        },
    },
};

</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
h3 {
    padding: $spacing-100;
}

.auth-status-section div:last-of-type {
    border-bottom: none;
}

.empty-state-container {
    display: flex;
    column-gap: $spacing-200;
    > * {
        max-width: px-to-rem(568);
    }

    @media($small) {
        flex-direction: column;
        > * {
            max-width: none;
        }
    }
}
</style>
